import "assets/css/reset.css";
import "assets/fonts/fonts.css";
import "assets/css/core.css";
import "assets/css/fonts.css";

import type * as Sentry from "@sentry/react";
import {breadcrumbsIntegration, browserTracingIntegration, init, thirdPartyErrorFilterIntegration} from '@sentry/react';
import {ErrorMessage} from "components/ErrorMessage";
import {GameErrors} from "components/GameErrorMessage";
import {HOCCountryCheck} from "components/HOCCountryCheck";
import {HOCDebugInformation} from "components/HOCDebugInformation";
import {HOCEntryPoint} from "components/HOCEntryPoint";
import {HOCi18n} from "components/HOCi18n";
import {HOCSession} from "components/HOCSession";
import {NotificationBanner} from "components/NotificationBanner";
import {TutorialModal} from "components/TutorialModal";
import {TutorialSecondModal} from "components/TutorialSecondModal";

import {store} from "modules/store";
import MulliganPage from "pages/homePage/MulliganPage";
import React from "react";
import {Provider} from "react-redux";
import {BrowserRouter} from "react-router-dom";
import styled from "@emotion/styled";
import {createRoot} from "react-dom/client";
import Routes from "routes";
import {ThemeProvider} from "@mui/material";
import {theme} from "assets/css/theme";
import Header from "components/Header";
import {HOCLiveScore} from "components/HOCLiveScore";
import {LiveScoreSection} from "modules/sagas/live_score";
import {HOCSecondarySecretKey, HOCSecretKey} from "components/HOCSecretKey";
import {HOCNavigate} from "components/HOCNavigate";
import {PromptModal} from "components/PromptModal";
import {RewardModal} from "components/RewardModal";
import ApiError from "modules/utils/Api/ApiError";
import type {AxiosError} from "axios";
import {SENTRY_DSN_URL, SENTRY_PROJECT_NAME} from 'modules';
import type {EventHint} from '@sentry/core/build/types/types-hoist/event';
import {ContextualPromptBanners} from 'components/ContextualPromptBanner';
import {WeeklyPrizeModal} from 'components/WeeklyPrizeModal';

let errorCount = 0;
const MAX_ERRORS = 100;

const IGNORE_ERRORS = [
	"Authentication Required",
	"Load failed",
	"UET is not defined",
	"Loading chunk",
	"AbortError: Share canceled",
	"captured as promise rejection",
	"reading 'insertBefore'",
	"Failed to fetch",
	"Network Error",
	"Minified Redux error",
	"Unexpected end of script",
	"Abort due to cancellation of share",
	"The network connection was lost.",
	"feature named `clickToLoad` was not found",
	"Maximum call stack size exceeded",
	"Possible side-effect in debug-evaluate",
	"Native bridge was not set",
	"Initialization Error: Native bridge was not set",
	"UnhandledRejection: Non-Error promise rejection captured with keys: message, status",
	"Non-Error promise rejection captured with keys: message, status",
	"TypeError: undefined is not an object (evaluating 'ceCurrentVideo.currentTime')",
	"Identifier MlbJavascriptDataStore has already been declared",
	"Identifier 'MlbJavascriptDataStore' has already been declared",
	"SyntaxError: Identifier MlbJavascriptDataStore has already been declared",
	"SyntaxError: Identifier 'MlbJavascriptDataStore' has already been declared",
	"Java exception was raised during method invocation",
	"Object captured as promise rejection with keys: message, status",
	"UnhandledRejection: Object captured as promise rejection with keys: message, status",
];

const extendSentryEvent = (event: Sentry.ErrorEvent, hint: Sentry.EventHint) => {
	const exception = hint.originalException as AxiosError;

	const isServerError = event.exception?.values?.find((exception) => {
		return exception?.value?.includes(`${ApiError.SERVER_ERROR}`);
	});

	if (isServerError) {
		event.extra = event.extra || {};
		try {
			event.extra["responseBtoa"] = btoa(JSON.stringify(exception.response?.data ?? {}));
			event.extra["headers"] = exception.response?.headers;
		} catch (e) {
			console.info(e);
		}

		event.extra["responseData"] = exception.response?.data;
	}
	return event;
};

if( SENTRY_DSN_URL ) {
	init({
		dsn: SENTRY_DSN_URL,
		integrations: [
			browserTracingIntegration(),
			breadcrumbsIntegration({
				console: false,
			}),
			thirdPartyErrorFilterIntegration({
				filterKeys: [SENTRY_PROJECT_NAME],
				behaviour: "drop-error-if-contains-third-party-frames",
			}),
		],
		release: "0.1.0",
		environment: import.meta.env.VITE_SENTRY_ENV || "development",
		allowUrls: [".loc", ".fanhubmedia.com", ".geniussports.com", "www.mlb.com", ".mlb.com"],
		ignoreErrors: IGNORE_ERRORS,
		denyUrls: [
			"quantcast",
			"xsca",
			// browser's extensions
			/extensions\//i,
			/^chrome:\/\//i,
			/^moz-extension:\/\//i,
		],
		beforeSend: (event: Sentry.ErrorEvent, hint: EventHint) => {
			errorCount++;
			if( errorCount > MAX_ERRORS ) {
				return null;
			}

			const isMonday = new Date().getDay() === 1;
			const isAuthError = event.exception?.values?.find((exception) => {
				return exception?.value?.includes(`${ApiError.AUTH_REQUIRED}`);
			});
			// Ignore all 401 errors for during the week since MLB app has some issues with cookies
			// Send errors only on Monday
			if( isAuthError && !isMonday ) {
				return null;
			}

			return extendSentryEvent(event, hint);
		},
		sampleRate: 0.1,
	});
}

const Content = styled.div`
	display: flex;
	flex-flow: column;

	@media (min-width: 879px) {
		flex: 1;
		overflow: hidden;
		height: 100%;
		height: -webkit-fill-available;
	}
`;

const container = document.getElementById("root");
if (container) {
	const root = createRoot(container);
	root.render(
		<React.StrictMode>
			<Provider store={store}>
				<ThemeProvider theme={theme}>
					<BrowserRouter basename={import.meta.env.VITE_BASE_URL}>
						<HOCDebugInformation>
							<HOCi18n>
								<HOCSecretKey>
									<HOCSecondarySecretKey>
										<HOCLiveScore section={LiveScoreSection.HOME}>
											<HOCCountryCheck>
												<HOCNavigate>
													<Content>
														<HOCSession>
															<Header />
															<NotificationBanner />
															<HOCEntryPoint>
																<Routes />
															</HOCEntryPoint>
															<WeeklyPrizeModal/>
														</HOCSession>
													</Content>
												</HOCNavigate>
												<ErrorMessage />
											</HOCCountryCheck>
											<GameErrors />
											<MulliganPage />
											<TutorialModal />
											<RewardModal />
											<TutorialSecondModal />
											<ContextualPromptBanners/>
											<PromptModal />
										</HOCLiveScore>
									</HOCSecondarySecretKey>
								</HOCSecretKey>
							</HOCi18n>
						</HOCDebugInformation>
					</BrowserRouter>
				</ThemeProvider>
			</Provider>
		</React.StrictMode>
	);
}
